import ModernMUITable from "../../../Components/ModernMUITable"
import { useModalProvider } from "../../../Providers/ModalProvider"
import CreateRoleModal from "./Subcomponents/CreateRoleModal"
import { useEndpointProvider } from "../../../Providers/EndpointProvider"
import { GridActionsCellItem, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid"
import EyeIcon from "../../../Components/Icons/EyeIcon"
import { MdOutlineModeEdit } from "react-icons/md"
import { useCallback, useState } from "react"
import ModifyRoleModal from "./Subcomponents/ModifyRoleModal"
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergTheme } from "../../../Moberg"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import ConfirmationModal from "../../../Components/ConfirmationModal/ConfirmationModal"
import { useAuthProvider } from "../../../Providers/AuthProvider"
import { permissionTooltip } from "../../../Utils/permissionUtils"

export default function RolesAndPermissions() {
	const { createModal, close } = useModalProvider()
	const endpointProvider = useEndpointProvider()
	const { LINKS } = useBackendLinksProvider()
	const queryClient = useQueryClient()
	const authProvider = useAuthProvider()
	const canManageRoles = authProvider.permissions?.['role:manage']
	
	const [selectedRoleIds, setSelectedRoleIds] = useState([])
	const [columnVisibilityModel, setColumnVisibilityModel] = useState({})

	const deleteDisabledReason = permissionTooltip(canManageRoles)
		|| (selectedRoleIds.length === 0 ? "Select roles to delete." : "")

	// Pagination state
	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 })

	const handleSelectionChange = (selectedIds) => {
		setSelectedRoleIds(selectedIds)
	}

	function createRoleModal() {
		createModal(<CreateRoleModal style={{ height: "80%" }} close={close} escClose={false} clickOutsideClose={false} />)
	}

	const deleteRoles = async (role_ids) => {
		return endpointProvider.post(LINKS.ADMIN.USER_MANAGEMENT.ROLES_AND_PERMISSIONS.DELETE_ROLES, { role_ids })
	}

	const deleteMutation = useMutation({
		mutationFn: deleteRoles,
		onSuccess: () => {
			setSelectedRoleIds([])
			queryClient.invalidateQueries({ queryKey: ["roles"] })
		},
		onError: (error) => {
			console.error('Error deleting roles:', error)
		}
	})

	const handleDeleteRoles = () => {
		if (selectedRoleIds.length === 0) return
		
		createModal(<ConfirmationModal
			title="Delete roles?"
			description={`Are you sure you want to permanently delete these ${selectedRoleIds.length} role${selectedRoleIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
			confirmButton={{
				text: "Yes, delete",
				theme: MobergTheme.RED,
				onClick: () => deleteMutation.mutate(selectedRoleIds)
			}}
		/>)
	}

	const handleClick = useCallback((row, viewMode) => {
		createModal(<ModifyRoleModal style={{ height: "80%" }} close={close} row={row} escClose={false} clickOutsideClose={false} viewMode={viewMode} />)
	}, [createModal, close])

	const columns = [
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			hideable: false,
		},
		{ field: "name", flex: 1, headerName: "Name", minWidth: 80, visible: true },
		{ field: "description", flex: 1, headerName: "Description", minWidth: 100, visible: true },
		{ field: "user_count", flex: 1, headerName: "No. users", valueGetter: (value, row) => row.user_count || 0, minWidth: 100, visible: true },
		{ field: "permission_count", flex: 1, headerName: "No. permissions", valueGetter: (value, row) => row.permission_count || 0, minWidth: 150, visible: true },
		{
			field: "action",
			flex: 0.4,
			headerName: "Actions",
			type: "actions",
			hideable: false,
			getActions: params => [
				<GridActionsCellItem icon={<EyeIcon />} label="View" onClick={() => handleClick(params.row, true)} showInMenu />,
				<GridActionsCellItem icon={<MdOutlineModeEdit size={20} />} label="Edit" onClick={() => handleClick(params.row)} showInMenu disabled={!canManageRoles} />,
			],
			minWidth: 80,
			visible: true
		},
	]

	const tableToolbarProps = {
		searchInput: { isShow: true },
		columnButton: { isShow: true },
		filterButton: { isShow: false },
		exportButton: { isShow: false },
		createButton: { isShow: false },
		deleteButton: { isShow: false },
		mainTable: true
	}

	const queryProps = {
		queryKey: "roles",
		endpoint: LINKS.ADMIN.USER_MANAGEMENT.USERS.GET_ROLES,
		body: {},
		optionalParams: {}
	}

	return (
		<div style={{ backgroundColor: "var(--color-surface-primary)" }}>
			<div style={{ padding: '10px 30px 10px', display: 'flex', gap: '12px', alignItems: 'center' }}>
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.BLUE}
					shape={MobergButtonShape.RECTANGLE}
					onClick={createRoleModal}
					disabled={!canManageRoles}
					tooltip={permissionTooltip(canManageRoles)}
				>
					Create role
				</MobergButton>
				
				<MobergButton
					variant={MobergButtonVariant.FILLED}
					theme={MobergTheme.RED}
					shape={MobergButtonShape.RECTANGLE}
					onClick={handleDeleteRoles}
					disabled={!!deleteDisabledReason}
					tooltip={deleteDisabledReason}
				>
					Delete ({selectedRoleIds.length})
				</MobergButton>
			</div>
			<div style={{ 
				position: 'relative', 
				padding: '0px 30px 20px', 
				height: 'calc(100vh - 200px)',
				minHeight: '500px',
				maxHeight: '80vh'
			}}>
				<ModernMUITable 
					columns={columns} 
					tableToolbarProps={tableToolbarProps} 
					{...queryProps}
					paginationMode="server"
					sortingMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={setPaginationModel}
				supportsPageSize={true}
					enableRowSelection={true}
					onSelectionChange={handleSelectionChange}
					isRowSelectable={() => canManageRoles}
					columnVisibilityModel={columnVisibilityModel}
					onColumnVisibilityModelChange={setColumnVisibilityModel}
					height="100%"
					persistStateInUrl={true}
					tableId="roles"
					defaultSortModel={[
						{ field: 'name', sort: 'asc' }
					]}
				/>
			</div>
		</div>
	)
}